import { useCallback, useEffect, useId, useRef, useState } from 'react'
import useUsernameSearch from '../hooks/useUsernameSearch'

/* Header "Search User" typeahead — the reference's vue-multiselect box, in
 * React, with the same markup and classes so app.css styles it unchanged
 * (.multiselect, --active, __tags, __input, __placeholder, __spinner,
 * __content-wrapper, __option, __option--highlight).
 *
 * Behaviour (reference's searchUser / search-change, tuned by the user
 * 2026-09-19):
 *   · the list opens only when the OPERATOR focuses/taps the field (no
 *     auto-focus when the mobile magnifier dropdown opens — that made the
 *     list appear before anyone touched the box)
 *   · nothing typed → "List is empty."
 *   · anything typed with no matches on screen → "No elements found" — that
 *     is what vue-multiselect shows for 1–2 chars too (reference screenshot
 *     "ko" → No elements found, user 2026-09-19)
 *   · from 3 chars the downline is searched (GET /admin/search-by-username)
 *     and matches list as soon as they arrive; still none → "No elements found"
 *   · a pick calls `onSelect(row)` and clears the box; the reference then opens
 *     its "search analysis" modal, which this backend does not have — Layout
 *     decides what a pick does (it takes the operator to the Account List
 *     filtered on that username).
 *
 * Kept out of Layout on purpose: Layout is already the whole shell, and the
 * search has its own state (query, results, open, highlight). Two instances
 * live in the header — desktop box + the mobile magnifier dropdown — so it must
 * be self-contained and cheap:
 *   · the request itself (3-letter minimum, debounce, abort of the stale fetch,
 *     no repeat of the same query) lives in hooks/useUsernameSearch, shared
 *     with the report pages' Search By Client Name field
 *   · the list is only mounted while open
 *   · keyboard: ↑ ↓ Enter Esc, mouse: hover highlights, click picks;
 *     click/tap outside closes
 */

const PAGE_LIMIT = 20

export default function HeaderUserSearch({ onSelect, placeholder = 'Search User', className = '', active = true }) {
  const listId = useId()
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  // true while a pointer that started INSIDE the box is still down — see onBlur
  const pointerInsideRef = useRef(false)

  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(0)

  const { results, loading, reset: resetSearch } = useUsernameSearch(query, { limit: PAGE_LIMIT })

  // a fresh list always starts on its first row
  useEffect(() => { setHighlight(0) }, [results])

  // ── open / close ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown, { passive: true })
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
    }
  }, [open])

  const reset = useCallback(() => {
    resetSearch()
    setQuery('')
    setHighlight(0)
    setOpen(false)
  }, [resetSearch])

  // `active` false = the container holding this box closed (mobile magnifier
  // dropdown) — wipe the query/results so it never reopens mid-search with a
  // stale list (user, 2026-09-19: state reset on every close).
  useEffect(() => {
    if (!active) reset()
  }, [active, reset])

  const pick = useCallback((row) => {
    if (!row) return
    onSelect?.(row)
    pointerInsideRef.current = false   // a pick's blur must really close
    reset()
    inputRef.current?.blur()
  }, [onSelect, reset])

  const onKeyDown = (e) => {
    // Enter never bubbles to the surrounding <form> (the mobile dropdown wraps
    // the box in one) — a submit there would reload the whole admin.
    if (e.key === 'Enter') {
      e.preventDefault()
      if (open) pick(results[highlight])
      return
    }
    if (!open) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => (results.length ? (h + 1) % results.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => (results.length ? (h - 1 + results.length) % results.length : 0))
    } else if (e.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
    }
  }

  const q = query.trim()
  // not `!loading`: hiding the row during the request emptied the list, so the
  // dropdown collapsed for ~300ms and sprang back with the results (flicker,
  // recording 2026-09-21). The reference keeps the line up until matches land.
  const showNoResult = open && q.length > 0 && results.length === 0
  const showEmpty = open && q.length === 0
  const showList = open

  return (
    <div
      ref={rootRef}
      tabIndex="-1"
      role="combobox"
      aria-owns={listId}
      aria-expanded={open ? 'true' : 'false'}
      className={`multiselect${open ? ' multiselect--active' : ''}${className ? ` ${className}` : ''}`}
      onClick={() => { setOpen(true); inputRef.current?.focus() }}
      // iOS: focus() only sticks inside a real user gesture, and a tap's click
      // arrives after it — the first tap opened nothing (same fix as
      // ClientMultiselect, 2026-09-22). touchstart is the gesture.
      onTouchStart={(e) => {
        pointerInsideRef.current = true
        if (e.target.closest('.multiselect__content-wrapper')) return
        if (open) return
        inputRef.current?.focus()
        setOpen(true)
      }}
      onTouchEnd={() => { pointerInsideRef.current = false }}
      onTouchCancel={() => { pointerInsideRef.current = false }}
      onMouseDownCapture={() => { pointerInsideRef.current = true }}
      onMouseUp={() => { pointerInsideRef.current = false }}
    >
      <div className="multiselect__select"></div>
      <div className="multiselect__tags">
        <div className="multiselect__tags-wrap" style={{ display: 'none' }}></div>
        <div className="multiselect__spinner" style={{ display: loading ? undefined : 'none' }}></div>
        <input
          ref={inputRef}
          name=""
          type="text"
          autoComplete="off"
          spellCheck="false"
          placeholder={placeholder}
          tabIndex="0"
          aria-controls={listId}
          className="multiselect__input"
          // vue-multiselect collapses the input to 0 while closed and shows the
          // placeholder span instead; open, the input takes the row.
          style={open ? { width: '100%' } : { width: '0px', position: 'absolute', padding: '0px' }}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          // The phone keyboard's Done (✓) only blurs the input — no tap lands
          // anywhere, so the outside listener never hears it and the list stayed
          // open. The reference closes on blur and drops what was typed
          // (vue-multiselect: @blur -> deactivate). Picks are safe: an option's
          // mousedown is preventDefault'ed, so it never blurs the input first.
          // a blur during the box's own opening gesture (pointer still down
          // inside) is not a leave — refocus; see ClientMultiselect
          onBlur={() => {
            if (pointerInsideRef.current) { inputRef.current?.focus(); return }
            reset()
          }}
          onKeyDown={onKeyDown}
        />
        {!open && <span className="multiselect__placeholder">{placeholder}</span>}
      </div>
      {/* mousedown kept off the input's focus (vue-multiselect: @mousedown.prevent)
          so dragging the list's scrollbar does not blur — and so close — the box */}
      <div tabIndex="-1" className="multiselect__content-wrapper" onMouseDown={(e) => e.preventDefault()} style={{ maxHeight: '300px', display: showList ? 'block' : 'none' }}>
        <ul role="listbox" id={listId} className="multiselect__content" style={{ display: 'block' }}>
          {results.map((row, i) => {
            const key = row.wallet_id ?? row.user_id ?? row.staff_id ?? row.username
            return (
              <li key={key} className="multiselect__element" role="option" aria-selected={i === highlight}>
                <span
                  className={`multiselect__option${i === highlight ? ' multiselect__option--highlight' : ''}`}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => { e.preventDefault(); pick(row) }}
                >
                  <span>{row.username}</span>
                </span>
              </li>
            )
          })}
          <li style={{ display: showNoResult ? undefined : 'none' }}><span className="multiselect__option"><span>No elements found</span></span></li>
          <li style={{ display: showEmpty ? undefined : 'none' }}><span className="multiselect__option">List is empty.</span></li>
        </ul>
      </div>
    </div>
  )
}
